/* ClaimProfileModal — Phase 1 · Claim + Verify
 * ─────────────────────────────────────────────────────────────────────
 * Modal + multi-step flow that lets a roster-synced athlete "claim"
 * their public profile. Once submitted, the profile flips from
 * `claim_status: "public"` → `"claimed"`, and admin reviews before
 * flipping to `"verified"`. Verified athletes get a magic-link portal
 * token so they can edit their profile going forward.
 *
 * Steps: 1) confirm identity → 2) email + phone → 3) social handles
 *        4) affidavit + submit
 */
import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  X, CheckCircle2, ShieldCheck, ChevronRight, ChevronLeft, Loader2,
  Upload, Lock, Camera, Check,
} from 'lucide-react';
import { useUserAuth } from '../../contexts/UserAuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Step = ({ n, cur, label }) => (
  <div className="flex items-center gap-2">
    <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-black
      ${cur > n ? 'bg-emerald-500 text-black' : cur === n ? 'bg-white text-black' : 'bg-white/10 text-white/40'}`}>
      {cur > n ? '✓' : n}
    </div>
    <span className={`text-[10px] uppercase tracking-widest font-bold ${cur >= n ? 'text-white' : 'text-white/30'}`}>{label}</span>
  </div>
);

const ClaimProfileModal = ({ athlete, onClose, onClaimed }) => {
  const { user, setLoginOpen } = useUserAuth();
  const needsAccount = !user;                     // no session → create account inline
  const [step, setStep] = useState(1);
  const [full_name, setName] = useState(athlete?.name || '');
  const [email, setEmail] = useState(user?.email || '');
  const [phone, setPhone] = useState('');
  // iter55 · Anonymous claimers create a FLYT account on submit — same
  // email is auto-attached to the verified profile on approval.
  const [password, setPassword] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [instagram, setInstagram] = useState('');
  const [hudl_url, setHudl] = useState('');
  const [maxpreps_url, setMaxpreps] = useState('');
  const [opendorse_url, setOpendorse] = useState('');
  const [affidavit, setAffidavit] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  // Step 4 · Identity verification — student ID or driver's license
  // photo, name printed on ID, and the privacy disclaimer checkbox.
  // The ID uploads through /api/claims/upload-id BEFORE the claim POST
  // so the founder sees the same photo attached to the review row.
  const [idType, setIdType] = useState('student_id');
  const [idFile, setIdFile] = useState(null);
  const [idPreview, setIdPreview] = useState('');
  const [idPhotoUrl, setIdPhotoUrl] = useState('');
  const [nameOnId, setNameOnId] = useState('');
  const [privacyOK, setPrivacyOK] = useState(false);
  const [uploadingId, setUploadingId] = useState(false);

  const emailValid = /.+@.+\..+/.test(email);
  const passwordOk = !needsAccount || (password.length >= 8 && password === confirmPw);
  const canGoStep2 = full_name.trim().length > 2;
  const canGoStep3 = emailValid && phone.trim().length >= 7 && passwordOk;
  // Verify step: photo uploaded to server + name-on-ID + privacy tick.
  const canGoStep5 = !!idPhotoUrl && nameOnId.trim().length > 1 && privacyOK && !uploadingId;
  const canSubmit = affidavit && canGoStep2 && canGoStep3 && canGoStep5;

  const uploadId = async (f) => {
    if (!f) return;
    if (!f.type?.startsWith('image/')) { toast.error('Please choose an image.'); return; }
    setIdFile(f);
    setIdPreview(URL.createObjectURL(f));
    setIdPhotoUrl('');       // clear stale URL until this one finishes
    setUploadingId(true);
    try {
      const fd = new FormData();
      fd.append('file', f);
      const r = await axios.post(`${API}/claims/upload-id`, fd);
      setIdPhotoUrl(r.data?.url || '');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Upload failed');
    } finally {
      setUploadingId(false);
    }
  };

  const submit = async () => {
    if (!canSubmit || submitting) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/athletes/${athlete.slug}/claim`, {
        full_name: full_name.trim(),
        email: email.trim(),
        phone: phone.trim(),
        // iter55 · If the claimer isn't logged in, we send a password so
        // the backend can create their account on approval and attach
        // the verified athlete slug to it. Existing accounts (email
        // already registered) simply have the slug linked on approval.
        password: needsAccount ? password : null,
        instagram: instagram.trim() || null,
        hudl_url: hudl_url.trim() || null,
        maxpreps_url: maxpreps_url.trim() || null,
        opendorse_url: opendorse_url.trim() || null,
        id_photo_url: idPhotoUrl || null,
        id_type: idType || null,
        name_on_id: nameOnId.trim() || null,
        disclaimer_accepted: privacyOK,
      });
      setDone(true);
      onClaimed?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not submit claim. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    // iter364 · Top safe-area padding so the header clears the iOS
    // notch. iter472 · Added generous bottom padding
    // (tab-bar height + home indicator) so the modal's Next / Submit
    // button never hides behind the FloatingNavIsland on iPhone.
    <div className="fixed inset-0 z-[999] bg-black/85 backdrop-blur-md flex items-start sm:items-center justify-center overflow-y-auto px-4 pt-[max(4rem,env(safe-area-inset-top))] pb-[calc(7rem+env(safe-area-inset-bottom))]" data-testid="claim-profile-modal">
      <div className="w-full max-w-lg bg-neutral-950 ring-1 ring-white/15 rounded-3xl overflow-hidden my-4 sm:my-8">
        <div className="px-6 py-4 flex items-center justify-between border-b border-white/10">
          <div className="flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-blue-400" />
            <p className="font-display uppercase tracking-widest text-sm text-white">Claim &amp; Verify your profile</p>
          </div>
          <button onClick={onClose} className="text-white/60 hover:text-white p-1" aria-label="Close" data-testid="claim-close"><X className="w-5 h-5" /></button>
        </div>

        {done ? (
          <div className="p-8 text-center">
            <CheckCircle2 className="w-16 h-16 text-emerald-400 mx-auto mb-4" />
            <h2 className="font-display text-2xl uppercase text-white mb-2">You&apos;re in the queue!</h2>
            <p className="text-sm text-gray-300 leading-relaxed max-w-md mx-auto">
              We got your claim for <span className="font-bold text-white">{athlete.name}</span>. Our team
              reviews new claims within the hour. When approved, it&apos;ll show up in the account you just created here — sign in with <span className="font-mono text-blue-300">{email}</span> anytime to check status.
            </p>
            {/* iter459e · Two-button CTA — primary "Log In Now"
                (per user request) sends the athlete straight to
                the login screen with their email pre-filled so
                they can log into the account they just created.
                Secondary "Got it" dismisses. */}
            <div className="mt-6 flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-2">
              <button
                onClick={() => {
                  // Close the claim modal first so the LoginModal
                  // lands cleanly on top of the profile screen (not
                  // stacked over a still-open claim overlay).
                  try { onClose && onClose(); } catch (_) { /* noop */ }
                  // Prefill the email the athlete used for the claim.
                  // LoginModal reads this off sessionStorage on open.
                  try {
                    if (email && typeof window !== 'undefined') {
                      window.sessionStorage.setItem('flyt:prefill_email', email);
                    }
                  } catch (_) { /* private mode */ }
                  // Small delay so the claim modal's close transition
                  // finishes before the LoginModal opens — otherwise
                  // both compete for focus on iOS Safari.
                  setTimeout(() => {
                    try { setLoginOpen && setLoginOpen(true); } catch (_) { /* noop */ }
                  }, 60);
                }}
                className="px-6 py-2.5 rounded-full bg-white text-black font-bold uppercase text-xs tracking-widest active:scale-[0.98] transition"
                data-testid="claim-done-login"
              >
                Log In Now
              </button>
              <button
                onClick={onClose}
                className="px-6 py-2.5 rounded-full bg-white/10 hover:bg-white/15 ring-1 ring-white/15 text-white font-bold uppercase text-xs tracking-widest active:scale-[0.98] transition"
                data-testid="claim-done-close"
              >
                Got it
              </button>
            </div>
          </div>
        ) : (
          <>
            {/* Step indicator */}
            <div className="px-6 py-3 flex items-center justify-between gap-2 bg-white/[0.02] border-b border-white/10 overflow-x-auto no-scrollbar">
              <Step n={1} cur={step} label="Identity" />
              <div className="flex-1 h-px bg-white/10 mx-1 min-w-[8px]" />
              <Step n={2} cur={step} label="Contact" />
              <div className="flex-1 h-px bg-white/10 mx-1 min-w-[8px]" />
              <Step n={3} cur={step} label="Platforms" />
              <div className="flex-1 h-px bg-white/10 mx-1 min-w-[8px]" />
              <Step n={4} cur={step} label="Verify" />
              <div className="flex-1 h-px bg-white/10 mx-1 min-w-[8px]" />
              <Step n={5} cur={step} label="Submit" />
            </div>

            <div className="p-6">
              {step === 1 && (
                <div className="space-y-4">
                  <div className="text-center pb-2">
                    <p className="text-xs uppercase tracking-widest text-blue-300 mb-1">Claiming</p>
                    <p className="font-display text-2xl uppercase text-white">{athlete.name}</p>
                    <p className="text-xs text-gray-400 mt-1">{[athlete.position, athlete.school_name || athlete.team].filter(Boolean).join(' · ')}</p>
                  </div>
                  <div>
                    <label className="text-[10px] uppercase tracking-widest text-white/50 font-bold block mb-1.5">Your full legal name</label>
                    <input type="text" value={full_name} onChange={(e) => setName(e.target.value)} placeholder="First Last"
                      className="w-full px-3 py-3 rounded-xl bg-white ring-1 ring-white/10 focus:ring-blue-400/50 outline-none"
                      style={{ color: '#000', WebkitTextFillColor: '#000', backgroundColor: '#fff', fontSize: 16, caretColor: '#000' }}
                      autoCapitalize="words" autoComplete="name"
                      data-testid="claim-name" />
                  </div>
                  <p className="text-[11px] text-gray-500 leading-snug">
                    We use your name to double-check the profile matches you. Nothing goes public without your permission.
                  </p>
                </div>
              )}
              {step === 2 && (
                <div className="space-y-4">
                  <div>
                    <label className="text-[10px] uppercase tracking-widest text-white/50 font-bold block mb-1.5">Email <span className="text-blue-300">(where we send your edit link)</span></label>
                    <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com"
                      className="w-full px-3 py-3 rounded-xl bg-white ring-1 ring-white/10 focus:ring-blue-400/50 outline-none"
                      style={{ color: '#000', WebkitTextFillColor: '#000', backgroundColor: '#fff', fontSize: 16, caretColor: '#000' }}
                      autoCapitalize="none" autoComplete="email" inputMode="email"
                      data-testid="claim-email" />
                  </div>
                  <div>
                    <label className="text-[10px] uppercase tracking-widest text-white/50 font-bold block mb-1.5">Phone</label>
                    <input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="(555) 123-4567"
                      className="w-full px-3 py-3 rounded-xl bg-white ring-1 ring-white/10 focus:ring-blue-400/50 outline-none"
                      style={{ color: '#000', WebkitTextFillColor: '#000', backgroundColor: '#fff', fontSize: 16, caretColor: '#000' }}
                      autoComplete="tel" inputMode="tel"
                      data-testid="claim-phone" />
                  </div>
                  <p className="text-[11px] text-gray-500 leading-snug">
                    We may text you to confirm identity. We never share or sell your contact info.
                  </p>
                  {needsAccount && (
                    <div className="pt-2 mt-1 border-t border-white/10 space-y-3">
                      <p className="text-[10px] uppercase tracking-widest text-blue-300 font-bold">Create your FLYT account</p>
                      <p className="text-[11px] text-gray-400 leading-snug -mt-1">
                        This email + password is how you&apos;ll sign into FLYT after you&apos;re verified. If the email is already registered, we&apos;ll link this profile to your existing account instead.
                      </p>
                      <div>
                        <label className="text-[10px] uppercase tracking-widest text-white/50 font-bold block mb-1.5">Password <span className="text-white/30">(8+ characters)</span></label>
                        <input
                          type="password"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          autoComplete="new-password"
                          placeholder="••••••••"
                          className="w-full px-3 py-3 rounded-xl bg-white ring-1 ring-white/10 focus:ring-blue-400/50 outline-none"
                      style={{ color: '#000', WebkitTextFillColor: '#000', backgroundColor: '#fff', fontSize: 16, caretColor: '#000' }}
                          data-testid="claim-password"
                        />
                      </div>
                      <div>
                        <label className="text-[10px] uppercase tracking-widest text-white/50 font-bold block mb-1.5">Confirm password</label>
                        <input
                          type="password"
                          value={confirmPw}
                          onChange={(e) => setConfirmPw(e.target.value)}
                          autoComplete="new-password"
                          placeholder="••••••••"
                          className="w-full px-3 py-3 rounded-xl bg-white ring-1 ring-white/10 focus:ring-blue-400/50 outline-none"
                      style={{ color: '#000', WebkitTextFillColor: '#000', backgroundColor: '#fff', fontSize: 16, caretColor: '#000' }}
                          data-testid="claim-password-confirm"
                        />
                        {confirmPw && password !== confirmPw && (
                          <p className="text-[11px] text-rose-300 mt-1">Passwords don&apos;t match.</p>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}
              {step === 3 && (
                <div className="space-y-3">
                  <p className="text-[11px] text-gray-400 leading-snug pb-1">
                    Optional — link any platforms where recruiters + fans already follow you. We use these to auto-verify your identity faster.
                  </p>
                  <div>
                    <label className="text-[10px] uppercase tracking-widest text-white/50 font-bold block mb-1.5">Instagram handle</label>
                    <input type="text" value={instagram} onChange={(e) => setInstagram(e.target.value)} placeholder="@yourhandle"
                      className="w-full px-3 py-3 rounded-xl bg-white ring-1 ring-white/10 focus:ring-blue-400/50 outline-none"
                      style={{ color: '#000', WebkitTextFillColor: '#000', backgroundColor: '#fff', fontSize: 16, caretColor: '#000' }} data-testid="claim-instagram" />
                  </div>
                  <div>
                    <label className="text-[10px] uppercase tracking-widest text-white/50 font-bold block mb-1.5">Hudl profile URL</label>
                    <input type="url" value={hudl_url} onChange={(e) => setHudl(e.target.value)} placeholder="https://www.hudl.com/profile/…"
                      className="w-full px-3 py-3 rounded-xl bg-white ring-1 ring-white/10 focus:ring-blue-400/50 outline-none"
                      style={{ color: '#000', WebkitTextFillColor: '#000', backgroundColor: '#fff', fontSize: 16, caretColor: '#000' }} data-testid="claim-hudl" />
                  </div>
                  <div>
                    <label className="text-[10px] uppercase tracking-widest text-white/50 font-bold block mb-1.5">MaxPreps profile URL</label>
                    <input type="url" value={maxpreps_url} onChange={(e) => setMaxpreps(e.target.value)} placeholder="https://www.maxpreps.com/athlete/…"
                      className="w-full px-3 py-3 rounded-xl bg-white ring-1 ring-white/10 focus:ring-blue-400/50 outline-none"
                      style={{ color: '#000', WebkitTextFillColor: '#000', backgroundColor: '#fff', fontSize: 16, caretColor: '#000' }} data-testid="claim-maxpreps" />
                  </div>
                  <div>
                    <label className="text-[10px] uppercase tracking-widest text-white/50 font-bold block mb-1.5">Opendorse profile URL</label>
                    <input type="url" value={opendorse_url} onChange={(e) => setOpendorse(e.target.value)} placeholder="https://opendorse.com/athletes/…"
                      className="w-full px-3 py-3 rounded-xl bg-white ring-1 ring-white/10 focus:ring-blue-400/50 outline-none"
                      style={{ color: '#000', WebkitTextFillColor: '#000', backgroundColor: '#fff', fontSize: 16, caretColor: '#000' }} data-testid="claim-opendorse" />
                  </div>
                </div>
              )}
              {step === 4 && (
                <div className="space-y-3" data-testid="claim-verify-step">
                  <div className="text-center pb-1">
                    <p className="text-xs uppercase tracking-widest text-blue-300 mb-1">Verify it&apos;s really you</p>
                    <p className="text-[11px] text-gray-400 leading-snug px-2">
                      Snap a photo of your student ID or driver&apos;s license so Jay can confirm you before you go live. Takes 10 seconds.
                    </p>
                  </div>
                  {/* ID type toggle */}
                  <div>
                    <label className="text-[10px] uppercase tracking-widest text-white/50 font-bold block mb-1.5">ID type</label>
                    <div className="grid grid-cols-2 gap-2">
                      {[
                        { id: 'student_id', label: 'Student ID', hint: 'High-school or college' },
                        { id: 'drivers_license', label: "Driver's License", hint: 'Or state photo ID' },
                      ].map((t) => (
                        <button
                          key={t.id}
                          type="button"
                          onClick={() => setIdType(t.id)}
                          className={`p-2.5 rounded-2xl ring-1 text-left transition ${idType === t.id ? 'bg-blue-500/15 ring-blue-400/50' : 'bg-white/5 ring-white/10 hover:bg-white/10'}`}
                          data-testid={`claim-idtype-${t.id}`}
                        >
                          <p className="text-xs font-bold uppercase tracking-wide text-white">{t.label}</p>
                          <p className="text-[10px] text-white/50 mt-0.5">{t.hint}</p>
                        </button>
                      ))}
                    </div>
                  </div>
                  {/* Photo uploader — two entry points so athletes on
                      iOS/Android/desktop all have a native path:
                      "Take photo" opens the camera directly,
                      "Upload" opens the photo library / file picker. */}
                  <div>
                    <label className="text-[10px] uppercase tracking-widest text-white/50 font-bold block mb-1.5">Photo of your ID</label>
                    <div className="relative h-36 rounded-2xl overflow-hidden ring-1 ring-white/10 bg-gradient-to-br from-blue-900/40 via-black to-purple-900/40" data-testid="claim-id-preview">
                      {idPreview ? (
                        <img src={idPreview} alt="ID" className="absolute inset-0 w-full h-full object-cover" style={{ filter: 'brightness(0.85)' }} />
                      ) : null}
                      <div className="absolute inset-0 inline-flex flex-col items-center justify-center gap-1 text-white/85 pointer-events-none">
                        {uploadingId ? (
                          <>
                            <Loader2 className="w-6 h-6 animate-spin" />
                            <span className="text-[10px] uppercase tracking-widest font-bold">Uploading…</span>
                          </>
                        ) : idPreview ? (
                          <>
                            <Camera className="w-5 h-5" />
                            <span className="text-[10px] uppercase tracking-widest font-bold">Preview</span>
                          </>
                        ) : (
                          <>
                            <Upload className="w-6 h-6" />
                            <span className="text-[11px] uppercase tracking-widest font-bold">JPG / PNG · max 10MB</span>
                          </>
                        )}
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-2 mt-2">
                      <label htmlFor="claim-id-camera"
                        className="cursor-pointer inline-flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-full bg-white/10 hover:bg-white/15 ring-1 ring-white/10 text-white text-[11px] uppercase tracking-widest font-bold transition"
                        data-testid="claim-id-camera-btn">
                        <Camera className="w-3.5 h-3.5" />
                        {idPreview ? 'Retake' : 'Take photo'}
                      </label>
                      <input id="claim-id-camera" type="file" accept="image/*" capture="environment" className="hidden"
                        onChange={(e) => uploadId(e.target.files?.[0])} data-testid="claim-id-camera-input" />
                      <label htmlFor="claim-id-upload"
                        className="cursor-pointer inline-flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-full bg-white/10 hover:bg-white/15 ring-1 ring-white/10 text-white text-[11px] uppercase tracking-widest font-bold transition"
                        data-testid="claim-id-upload-btn">
                        <Upload className="w-3.5 h-3.5" />
                        {idPreview ? 'Replace' : 'Upload'}
                      </label>
                      <input id="claim-id-upload" type="file" accept="image/*" className="hidden"
                        onChange={(e) => uploadId(e.target.files?.[0])} data-testid="claim-id-upload-input" />
                    </div>
                  </div>
                  {/* Name on ID */}
                  <div>
                    <label className="text-[10px] uppercase tracking-widest text-white/50 font-bold block mb-1.5">Name printed on ID</label>
                    <input type="text" value={nameOnId} onChange={(e) => setNameOnId(e.target.value)} placeholder="Kellen Adams"
                      className="w-full px-3 py-3 rounded-xl bg-white ring-1 ring-white/10 focus:ring-blue-400/50 outline-none"
                      style={{ color: '#000', WebkitTextFillColor: '#000', backgroundColor: '#fff', fontSize: 16, caretColor: '#000' }} data-testid="claim-name-on-id" />
                  </div>
                  {/* Disclaimer — iter472 · Custom-styled checkbox
                      (white outline, blue fill on check) so it's
                      visible against the dark iOS UI. Native
                      accent-color falls back to invisible black on
                      some iOS builds. */}
                  <label className="flex items-start gap-2.5 cursor-pointer select-none" data-testid="claim-privacy-label">
                    <input type="checkbox" checked={privacyOK} onChange={(e) => setPrivacyOK(e.target.checked)}
                      className="sr-only" data-testid="claim-privacy-checkbox" />
                    <span
                      aria-hidden="true"
                      className={`mt-0.5 shrink-0 w-5 h-5 rounded-md border-2 flex items-center justify-center transition ${privacyOK ? 'bg-blue-500 border-blue-500' : 'bg-transparent border-white'}`}
                    >
                      {privacyOK && <Check className="w-3.5 h-3.5 text-white" strokeWidth={3} />}
                    </span>
                    <span className="text-[11px] text-white/70 leading-snug flex-1">
                      <Lock className="w-3 h-3 inline mr-1 text-white/50" />
                      Flythouse will only use this photo to verify your identity. We <span className="font-bold text-white">will not share it</span>, sell it, or reuse it for anything else.
                    </span>
                  </label>
                </div>
              )}
              {step === 5 && (
                <div className="space-y-4">
                  <div className="rounded-2xl bg-white/5 ring-1 ring-white/10 p-4">
                    <p className="text-[10px] uppercase tracking-widest text-blue-300 mb-2">Review</p>
                    <p className="text-sm text-white"><span className="text-white/50">Athlete:</span> {athlete.name}</p>
                    <p className="text-sm text-white"><span className="text-white/50">Name:</span> {full_name}</p>
                    <p className="text-sm text-white"><span className="text-white/50">Email:</span> {email}</p>
                    <p className="text-sm text-white"><span className="text-white/50">Phone:</span> {phone}</p>
                    {instagram && <p className="text-sm text-white"><span className="text-white/50">IG:</span> {instagram}</p>}
                    {hudl_url && <p className="text-sm text-white"><span className="text-white/50">Hudl:</span> ✓ linked</p>}
                    {maxpreps_url && <p className="text-sm text-white"><span className="text-white/50">MaxPreps:</span> ✓ linked</p>}
                    {opendorse_url && <p className="text-sm text-white"><span className="text-white/50">Opendorse:</span> ✓ linked</p>}
                    <p className="text-sm text-white mt-1"><span className="text-white/50">ID:</span> ✓ {idType === 'student_id' ? 'Student ID' : "Driver's License"} · {nameOnId}</p>
                  </div>
                  <label className="flex items-start gap-3 cursor-pointer select-none">
                    <input type="checkbox" checked={affidavit} onChange={(e) => setAffidavit(e.target.checked)}
                      className="sr-only" data-testid="claim-affidavit" />
                    <span
                      aria-hidden="true"
                      className={`mt-0.5 shrink-0 w-5 h-5 rounded-md border-2 flex items-center justify-center transition ${affidavit ? 'bg-blue-500 border-blue-500' : 'bg-transparent border-white'}`}
                    >
                      {affidavit && <Check className="w-3.5 h-3.5 text-white" strokeWidth={3} />}
                    </span>
                    <span className="text-xs text-gray-300 leading-snug">
                      I confirm that I am <span className="font-bold text-white">{athlete.name}</span>. I understand that
                      submitting a false claim may result in permanent removal from the Flythouse network, and that any
                      NIL (Name, Image, Likeness) activity I pursue through Flythouse remains subject to my school, state,
                      conference, and (where applicable) NCAA compliance rules — Flythouse does not guarantee eligibility or compensation.
                    </span>
                  </label>
                </div>
              )}
            </div>

            {/* Navigation */}
            <div className="p-4 pt-2 border-t border-white/10 flex items-center justify-between">
              <button
                onClick={() => (step > 1 ? setStep(step - 1) : onClose())}
                className="text-xs uppercase tracking-widest text-white/60 hover:text-white flex items-center gap-1"
                data-testid="claim-back"
              >
                <ChevronLeft className="w-4 h-4" /> {step === 1 ? 'Cancel' : 'Back'}
              </button>
              {step < 5 ? (
                <button
                  onClick={() => setStep(step + 1)}
                  disabled={
                    (step === 1 && !canGoStep2)
                    || (step === 2 && !canGoStep3)
                    || (step === 4 && !canGoStep5)
                  }
                  className="px-4 py-2 rounded-full bg-white text-black font-bold uppercase text-xs tracking-widest disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1"
                  data-testid="claim-next"
                >
                  Next <ChevronRight className="w-4 h-4" />
                </button>
              ) : (
                <button
                  onClick={submit}
                  disabled={!canSubmit || submitting}
                  className="px-4 py-2 rounded-full bg-blue-500 hover:bg-blue-600 text-white font-bold uppercase text-xs tracking-widest disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1.5 transition"
                  data-testid="claim-submit"
                >
                  {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Submitting…</> : <>Submit for Verification <ShieldCheck className="w-4 h-4" /></>}
                </button>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ClaimProfileModal;
